import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Copy, Check, Gift, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { getApiBaseUrl } from '@/lib/apiBase';

const API_URL = getApiBaseUrl();

// Shares the /share/stats data source with UserDashboard.js's "Share & Earn" tab
// (real fields: link, enabled, verified_count, shares_required, reward_percent,
// rewards[], whatsapp_message) — this is a differently-styled entry point onto
// the same underlying feature, not a separate one.
export default function ShareEarnSection({ user }) {
  const navigate = useNavigate();
  const [shareStats, setShareStats] = useState(null);
  const [loading, setLoading] = useState(!!user);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/share/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setShareStats(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const handleCopy = () => {
    if (!shareStats?.link) return;
    navigator.clipboard.writeText(shareStats.link);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleWhatsAppShare = async () => {
    if (!shareStats?.whatsapp_message) return;
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API_URL}/share/tap`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* analytics-only, never block the share */ }
    window.open(`https://wa.me/?text=${encodeURIComponent(shareStats.whatsapp_message)}`, '_blank', 'noopener,noreferrer');
  };

  // Admin can disable Share & Earn globally — nothing to show then.
  if (user && !loading && shareStats && !shareStats.enabled) return null;

  const shareProgress = shareStats?.verified_count || 0;
  const shareTarget = shareStats?.shares_required || 10;
  const sharePercent = Math.min((shareProgress / shareTarget) * 100, 100);
  const latestReward = shareStats?.rewards?.[0];

  return (
    <Card
      className="max-w-[480px] mx-auto border-2 border-green-200 dark:border-green-900/50 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/10"
      data-testid="share-earn-section"
    >
      <CardContent className="pt-6 text-center">
        <p className="text-[11px] font-bold uppercase tracking-wide text-green-700 dark:text-green-400 mb-1">
          💰 Can't afford it now?
        </p>
        <h3 className="text-lg font-bold mb-1">Share & Earn a Discount</h3>
        <p className="text-sm text-muted-foreground mb-5">
          Share with {shareTarget} friends on WhatsApp → earn a {shareStats?.reward_percent ?? 10}% off coupon
        </p>

        {!user && (
          <Button
            onClick={() => navigate('/login', { state: { from: { pathname: '/pricing' } } })}
            className="w-full bg-[#25D366] hover:bg-[#1ebe5b] text-white"
          >
            Login to Get Your Share Link
          </Button>
        )}

        {user && loading && (
          <div className="animate-pulse h-10 bg-muted rounded-md" />
        )}

        {user && !loading && shareStats && (
          <>
            {latestReward && (
              <div className="mb-5 rounded-lg border-2 border-green-400 bg-green-50 dark:bg-green-950/30 p-4 text-left">
                <p className="text-center text-2xl mb-1">🎉</p>
                <p className="text-center font-bold text-green-700 dark:text-green-400 mb-1">
                  You've earned a discount!
                </p>
                <p className="text-center text-sm flex items-center justify-center gap-2 flex-wrap">
                  <Gift className="w-4 h-4 text-green-600" />
                  Apply code
                  <span className="font-mono font-semibold bg-green-100 dark:bg-green-900/40 px-2 py-0.5 rounded">
                    {latestReward.coupon_code}
                  </span>
                  at checkout
                </p>
              </div>
            )}

            <div className="flex gap-2 mb-5 text-left">
              <input
                readOnly
                value={shareStats.link || ''}
                className="flex-1 min-w-0 rounded-md border border-input bg-background px-3 py-2 text-xs font-mono"
                data-testid="share-earn-link-input"
              />
              <Button onClick={handleCopy} variant="outline" size="icon" className="shrink-0" title="Copy link">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>

            <div className="mb-5 text-left">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-semibold">Your Progress</span>
                <span className="text-sm font-bold text-green-700 dark:text-green-400">
                  {shareProgress} / {shareTarget} verified clicks
                </span>
              </div>
              <div className="h-2.5 rounded-full bg-green-100 dark:bg-green-900/40 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-green-500 to-emerald-600 transition-all duration-1000"
                  style={{ width: `${sharePercent}%` }}
                />
              </div>
            </div>

            <Button
              onClick={handleWhatsAppShare}
              className="w-full bg-[#25D366] hover:bg-[#1ebe5b] text-white mb-4"
              data-testid="share-earn-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Share on WhatsApp
            </Button>

            <div className="text-left space-y-1">
              {[
                'No purchase needed to start sharing',
                'Coupon applied automatically at checkout',
                'Earn unlimited coupons — share again after reward',
              ].map((text) => (
                <p key={text} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="text-green-600 font-bold">✓</span>
                  {text}
                </p>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
